// Pure helpers for RichTextEditor. Non-JSX module so the unit tests can
// import without pulling React/TSX through the root tsconfig — same
// pattern as email-editor-helpers.ts.

import { isAllowedRichTextUrl } from "@shared/email/rich-text-allowlist";
import {
  LINK_MERGE_TOKEN_DEFAULTS,
  linkTextForBody,
} from "@shared/microsoft-graph/merge-fields";

/** The formatting commands the toolbar exposes, in toolbar order. */
export type RichTextCommand =
  | "bold"
  | "italic"
  | "underline"
  | "insertUnorderedList"
  | "insertOrderedList"
  | "removeFormat";

export interface ToolbarButtonSpec {
  command: RichTextCommand;
  /** Lucide icon name, resolved through the shared CatalogIcon. */
  icon: string;
  /** Accessible name + tooltip. */
  label: string;
  /** Shown after the label in the tooltip when the platform has one. */
  shortcut?: string;
}

export const RICH_TEXT_TOOLBAR: ToolbarButtonSpec[] = [
  { command: "bold", icon: "Bold", label: "Bold", shortcut: "Ctrl+B" },
  { command: "italic", icon: "Italic", label: "Italic", shortcut: "Ctrl+I" },
  {
    command: "underline",
    icon: "Underline",
    label: "Underline",
    shortcut: "Ctrl+U",
  },
  { command: "insertUnorderedList", icon: "List", label: "Bulleted list" },
  { command: "insertOrderedList", icon: "ListOrdered", label: "Numbered list" },
  { command: "removeFormat", icon: "RemoveFormatting", label: "Clear formatting" },
];

/**
 * Turn what a user typed into a link dialog into a URL we'd accept, or
 * null when it can't be salvaged.
 *
 * People paste "stewardship.pro" far more often than they type a scheme,
 * so a bare host/path gets https:// prepended rather than being rejected
 * — but only when it actually looks like a hostname. An input that
 * already carries a scheme is never rewritten: silently upgrading
 * "javascript:alert(1)" into "https://javascript:alert(1)" would turn a
 * clear rejection into a confusing broken link, and rewriting "http://"
 * to https would break intranet links that genuinely aren't TLS.
 */
export function normalizeLinkUrl(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;

  // An email address typed bare becomes a mailto: link — that's what
  // someone means when they type it into a link box.
  if (/^[^\s@:/]+@[^\s@:/]+\.[A-Za-z]{2,}$/.test(trimmed)) {
    return `mailto:${trimmed}`;
  }

  // Already carries a scheme → accept or reject as-is, never rewrite.
  if (/^[A-Za-z][A-Za-z0-9+.-]*:/.test(trimmed)) {
    return isAllowedRichTextUrl(trimmed) ? trimmed : null;
  }

  // Scheme-less: only prepend https:// when the leading segment looks
  // like a hostname (has a dot, no spaces). "click here" must not
  // become "https://click here".
  const host = trimmed.split(/[/?#]/)[0] ?? "";
  if (!/^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/.test(host)) return null;
  const candidate = `https://${trimmed}`;
  return isAllowedRichTextUrl(candidate) ? candidate : null;
}

export const LINK_URL_REJECTED_MESSAGE =
  "That doesn't look like a web address. Try something like stewardship.pro or https://stewardship.pro.";

/**
 * Wrap a merge token for insertion. Kept separate from the plain-text
 * editor's wrapToken because the rich-text editor inserts it as TEXT
 * (via execCommand("insertText")), which means the browser escapes it
 * for us — the braces must NOT be pre-escaped here or the author would
 * see "&#123;&#123;".
 */
export function mergeTokenInsertText(token: string): string {
  return `{{${token}}}`;
}

// ─── Link tokens as chips (2026-09-04, the email-step redesign) ───────
//
// Brian's feedback: a booking link inserted into the body landed as the
// raw text "{{booking.link}}", and nothing on the screen said what it
// was, who it booked with, or how to change its words. The editor now
// draws the URL-valued tokens — the ones the send path turns into real
// anchors (MergeTokenDef.isLink) — as labeled, non-editable chips that
// show the words the client will click. The STORED body is unchanged:
// chips exist only in the contentEditable; on the way out they turn
// back into the same {{token}} text every rule has always carried.
//
// Only link tokens get the chip treatment. A first-name token reads
// fine inline as "{{contact.preferred_name}}" next to its sample
// preview; a link is different because its words are a setting the
// author chose (or forgot to), and the chip is where that setting shows.

/** The tokens the send path renders as anchors, keyed to their default
 *  wording. Same source as the engine, so the set can't drift. */
export function isLinkMergeToken(token: string): boolean {
  return Object.prototype.hasOwnProperty.call(LINK_MERGE_TOKEN_DEFAULTS, token);
}

/**
 * The words a link chip shows for `token`, given the rule's config —
 * exactly the words the real send will put on the anchor (both read
 * through linkTextForBody). Null for a token that is not a link.
 */
export function linkChipLabel(
  token: string,
  config: { link_text?: string | undefined; booking_link_text?: string | undefined },
): string | null {
  if (!isLinkMergeToken(token)) return null;
  return linkTextForBody(config.link_text ?? null, config.booking_link_text ?? null)[token] ?? null;
}

export type MergeTextSegment = { kind: "text"; text: string } | { kind: "token"; token: string };

/**
 * Split a run of text into literal text and {{token}} segments, in
 * order. Matches the same {{ path }} shape the resolver does (optional
 * inner whitespace), so a token the send path would fill is exactly a
 * token the editor draws.
 */
export function splitByMergeTokens(text: string): MergeTextSegment[] {
  const out: MergeTextSegment[] = [];
  const re = /\{\{\s*([\w.]+)\s*\}\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ kind: "text", text: text.slice(last, m.index) });
    out.push({ kind: "token", token: m[1] ?? "" });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** True when the body (or subject) carries `token` anywhere. */
export function bodyHasMergeToken(text: string, token: string): boolean {
  return new RegExp(`\\{\\{\\s*${token.replace(/\./g, "\\.")}\\s*\\}\\}`).test(text);
}

/**
 * Remove every occurrence of `token` from the body, plus one following
 * space so "click {{booking.link}} to book" doesn't leave a double
 * space. The "Remove link" affordance on a chip's settings panel. Pure:
 * works on the stored HTML string, not the editor DOM, so a removal is
 * a plain config change the editor then adopts like any other.
 */
export function bodyWithoutMergeToken(text: string, token: string): string {
  const escaped = token.replace(/\./g, "\\.");
  return text.replace(new RegExp(`\\{\\{\\s*${escaped}\\s*\\}\\} ?`, "g"), "");
}

/** The class + data attribute the editor stamps on a link chip. Shared
 *  with the tests so a renamed attribute can't silently break them. */
export const LINK_CHIP_CLASS = "rt-chip";
export const LINK_CHIP_ATTR = "data-merge-token";
